import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';
import { integerToCurrency } from '@actual-app/core/shared/util';

import { Page } from '#components/Page';
import { useLocale } from '#hooks/useLocale';

import { FormularioCompromisso } from './compromissos/FormularioCompromisso';
import { cartao, cor, espaco, tela, texto } from './estilo';
import { montarContasFixas, totalPrevisto } from './motor/contasFixas';
import type { Escopo } from './motor/escopo';
import { ROTULO_ESCOPO } from './motor/escopo';
import { useAgendadosDoMes } from './motor/useAgendadosDoMes';
import { useMesFinanceiro } from './motor/useMesFinanceiro';

/**
 * "Compromissos" (`/compromissos`) — cadastrar um fixo em três campos: nome,
 * valor, dia do mês.
 *
 * ⭐ É a resposta direta à queixa "isso tem cara de ferramenta de
 * contabilidade": por baixo, `FormularioCompromisso` grava o MESMO agendado
 * nativo que a tela `/schedules` do Actual cria — só que sem pedir pra
 * escolher operador de condição, conta, forma de repetição etc. Quem quiser
 * esse controle fino continua tendo, em Ajustes ▸ Contas fixas (`/schedules`,
 * já listado em `AtalhosAjustes.tsx`).
 *
 * A leitura do resumo do mês e a lista reaproveitam o MESMO motor da Task 10
 * (`motor/contasFixas.ts`, `motor/useAgendadosDoMes.ts`) que `Inicio.tsx` já
 * usa em "Contas do mês" — nenhuma consulta nova.
 *
 * ⚠️⚠️ Divergência do brief (registrada no relatório): o brief original
 * dava `key={versao}` diretamente na `<View>` de dentro deste MESMO
 * componente. Isso não recarrega nada: `useMesFinanceiro`/
 * `useAgendadosDoMes` são chamados no CORPO de `Compromissos`, e um `key`
 * num descendente não afeta o ciclo de vida de hooks do ANCESTRAL — o
 * `useEffect` de dentro dos dois hooks só reexecuta quando `mes`/`escopo`
 * mudam de valor, o que `versao` nunca faz. Resultado: criar um
 * compromisso e apertar "Adicionar" não mostraria o novo item até um F5.
 * A correção é subir o `key` um nível: `Compromissos` só guarda `versao` e
 * remonta `CompromissosConteudo` (onde os hooks vivem) por baixo dele —
 * remontar de verdade dispara os `useEffect` de novo, porque eles rodam
 * incondicionalmente na montagem.
 *
 * ⚠️⚠️ FIX (validação no navegador, 22/09/2026) — o MESMO remount que corrige
 * a lista quebrava o aviso de "vencimento em outro mês": esse aviso morava
 * em `useState` DENTRO de `FormularioCompromisso`, que vive dentro da árvore
 * remontada. `salvar()` chamava `setAviso(msg)` e, na sequência (mesmo
 * `async function`, já depois do `await send(...)`), `onCriado()` — que troca
 * `versao` e remonta `CompromissosConteudo` de baixo pra cima, destruindo a
 * instância de `FormularioCompromisso` que acabou de setar o aviso.
 * Resultado: o aviso nascia e morria no mesmo commit, nunca chegava a pintar
 * a tela. A correção é a mesma receita do `versao`: o estado que precisa
 * SOBREVIVER ao remount mora aqui, em `Compromissos` (que nunca remonta), e
 * desce como prop controlada para `FormularioCompromisso`.
 */
export function Compromissos() {
  const [versao, setVersao] = useState(0);
  const [aviso, setAviso] = useState<string | null>(null);
  return (
    <CompromissosConteudo
      key={versao}
      onCriado={() => setVersao(v => v + 1)}
      aviso={aviso}
      onAvisoChange={setAviso}
    />
  );
}

function CompromissosConteudo({
  onCriado,
  aviso,
  onAvisoChange,
}: {
  onCriado: () => void;
  aviso: string | null;
  onAvisoChange: (aviso: string | null) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const mes = monthUtils.currentMonth();
  const escopo: Escopo = 'pessoal';

  const { linhas, erro: erroMes } = useMesFinanceiro(mes, escopo);
  const { agendados, carregando, erro } = useAgendadosDoMes(
    mes,
    linhas,
    escopo,
  );
  const contas = montarContasFixas(agendados, linhas, escopo);
  const previsto = totalPrevisto(contas);
  const pago = contas.reduce((soma, c) => soma + (c.realizado ?? 0), 0);

  return (
    <Page header={t('Compromissos')}>
      <View style={tela} data-testid="tela-compromissos">
        {/*
         * ⚠️⚠️ Revisão final da branch (Finding 4, Important) — mesma
         * correção mínima de `Medias.tsx`: esta tela fixa mês corrente +
         * escopo pessoal sem avisar disso. Ver o JSDoc lá para o porquê.
         */}
        <Text
          data-testid="compromissos-periodo"
          style={{ ...texto.corpo, color: cor.tintaFraca }}
        >
          <Text style={{ textTransform: 'capitalize' }}>
            {monthUtils.format(mes, 'MMMM yyyy', locale)}
          </Text>
          {' · '}
          {t(ROTULO_ESCOPO[escopo])}
        </Text>

        <View style={cartao} data-testid="compromissos-resumo">
          <Text style={texto.rotuloCartao}>
            <Trans>Este mês</Trans>
          </Text>
          <Text style={texto.valorCartao}>
            {t('{{pago}} pagos de {{previsto}}', {
              pago: integerToCurrency(pago),
              previsto: integerToCurrency(pago + previsto),
            })}
          </Text>
        </View>

        <FormularioCompromisso
          onCriado={onCriado}
          aviso={aviso}
          onAvisoChange={onAvisoChange}
        />

        {(erro || erroMes) && (
          <Text style={{ color: cor.saida }}>
            {t('Não consegui ler seus compromissos: {{erro}}', {
              erro: (erro ?? erroMes)?.message ?? '',
            })}
          </Text>
        )}

        {carregando && (
          <Text>
            <Trans>Carregando…</Trans>
          </Text>
        )}

        {!carregando && !erro && contas.length === 0 && (
          <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
            {t('Nenhum compromisso ainda. Use o formulário acima.')}
          </Text>
        )}

        {/*
         * ⚠️ Divergência do brief da Task 7 (registrada no relatório): o
         * brief original envolvia cada compromisso no PRÓPRIO `cartao`
         * (borda inteira por item). Isso contraria a regra nº 1 da estética
         * do dono ("nunca separar blocos por borda solta") e o padrão já
         * estabelecido em `inicio/ContasDoMes.tsx` e
         * `patrimonio/LinhaDeConta.tsx`/`Patrimonio.tsx`: UMA lista agrupa
         * todas as linhas dentro de um único `cartao`, e o respiro entre
         * linhas vem da ALTURA (`espaco.alturaLinha`), nunca de uma borda
         * por linha.
         */}
        {contas.length > 0 && (
          <View style={{ ...cartao, gap: 4 }} data-testid="compromissos-lista">
            <Text style={texto.tituloBloco}>
              <Trans>Seus compromissos</Trans>
            </Text>

            {contas.map(c => (
              <View
                key={c.id}
                data-testid={`compromisso-${c.id}`}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  height: espaco.alturaLinha,
                }}
              >
                <View>
                  <Text style={texto.corpo}>{c.nome}</Text>
                  {c.vencimento && (
                    <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
                      {t('vence dia {{dia}}', {
                        dia: c.vencimento.slice(-2),
                      })}
                    </Text>
                  )}
                </View>

                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={texto.valorMedio}>
                    {integerToCurrency(c.realizado ?? c.planejado)}
                  </Text>
                  <Text
                    style={{
                      ...texto.corpo,
                      color: c.realizado === null ? cor.saida : cor.entrada,
                    }}
                  >
                    {c.realizado === null ? t('a pagar') : t('pago')}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    </Page>
  );
}
